<template>
  <div>
    <b-modal
      ref="modalDelete"
      title="Are you sure you want to permanently delete this backup?"
      centered
      ok-title="Delete"
      ok-variant="danger"
      @ok="deleteRow"
      @hidden="recordToDelete = null"
    >
      <div>
        Deleted backups cannot be recovered later.
      </div>
    </b-modal>
    <div
      v-if="$can('create', 'site-backups')"
      class="d-flex justify-content-end mb-1"
    >
      <create-backup-dropdown
        :site-id="siteId"
        :disabled="createDisabled"
        :is-controller-online="isControllerOnline"
        @created="fetchItems"
      />
    </div>
    <table-mobile-solo
      :handle-search="null"
      :loading="isLoading"
      :items="rows"
      key-value="heading"
      key-prop="id"
      custom-class="no-padding"
      :fields="mobileFields"
      :pagination="{
        pagination: pagination,
        perPage: listParams.perPage,
        currentPage: listParams.page,
        pageSizeOptions: pageSizeOptions,
        enabled: true,
        perPageChanged: perPageChanged,
        changePage: changePage,
      }"
      :actions="[{
        label: 'View',
        type: 'primary',
        icon: 'EyeIcon',
        visible: item => item.status === STATUS_DONE,
        click: item => viewBackup(item.id),
      }, {
        label: 'Delete',
        type: 'danger',
        icon: 'TrashIcon',
        visible: () => $can('delete', 'site-backups'),
        click: item => deleteHandler(item),
      }]"
    />
    <vue-good-table
      :is-loading="isLoading"
      :columns="fields"
      :rows="rows"
      mode="remote"
      class="hide-mobile-table site-backups-table"
      compact-mode
      :total-rows="pagination.total"
      :pagination-options="{
        enabled: true,
        perPage: listParams.perPage,
        setCurrentPage: listParams.page,
      }"
      :sort-options="{ enabled: true }"
      @on-sort-change="onSortChange"
      @on-column-filter="onColumnFilter"
    >
      <template slot="loadingContent">
        <b-spinner
          variant="primary"
          label="Spinning"
        />
      </template>
      <template
        slot="table-row"
        slot-scope="props"
      >
        <span v-if="props.column.field === 'title'">
          <b-link
            v-if="props.row.status === STATUS_DONE"
            :to="{ name: 'site-summary-backup-details', params: { id: siteId, backupId: props.row.id } }"
          >
            {{ props.row.title }}
          </b-link>
          <span v-else>{{ props.row.title }}</span>
        </span>
        <span v-else-if="props.column.field === 'status'">
          <b-badge
            pill
            :variant="statusVariants[props.row.status]"
          >
            {{ props.row.status_label }}
          </b-badge>
        </span>
        <span v-else-if="props.column.field === 'action'">
          <b-dropdown
            variant="link"
            toggle-class="text-decoration-none"
            no-caret
          >
            <template v-slot:button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="text-body align-middle mr-25"
              />
            </template>
            <b-dropdown-item
              :disabled="props.row.status !== STATUS_DONE"
              @click="viewBackup(props.row.id)"
            >
              <feather-icon
                icon="EyeIcon"
                class="mr-50"
              />
              <span>View</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="$can('delete', 'site-backups')"
              @click="deleteHandler(props.row)"
            >
              <feather-icon
                icon="TrashIcon"
                class="mr-50"
              />
              <span>Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </span>
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <template slot="pagination-bottom">
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap">
              Showing 1 to
            </span>
            <b-form-select
              v-model="listParams.perPage"
              :options="pageSizeOptions"
              class="mx-1"
              @change="perPageChanged"
            />
            <span class="text-nowrap"> of {{ pagination.total }} entries </span>
          </div>
          <div>
            <b-pagination
              v-if="pagination.last_page > 1"
              :value="listParams.page"
              :total-rows="pagination.total"
              :per-page="listParams.perPage"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @change="changePage"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  BDropdown, BDropdownItem, BSpinner, BPagination, BFormSelect, BBadge, BLink, BModal,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import TableMobileSolo from '@/components/TableMobileSolo.vue'
import CreateBackupDropdown from '../sites/backups/CreateBackupDropdown.vue'
import {
  STATUS_DONE, STATUS_PENDING, statusLabels, statusVariants, sourceLabels, formatDateTime,
} from './helpers'

const POLL_INTERVAL = 5000

const filterFields = {
  title: 'title:like',
  source: 'source:=',
  status: 'status:=',
}

export default {
  components: {
    BDropdown,
    BDropdownItem,
    BSpinner,
    BPagination,
    BFormSelect,
    BBadge,
    BLink,
    BModal,
    VueGoodTable,
    TableMobileSolo,
    CreateBackupDropdown,
  },
  props: {
    siteId: {
      required: true,
      type: [String, Number],
    },
    createDisabled: {
      type: Boolean,
      default: false,
    },
    isControllerOnline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      STATUS_DONE,
      statusVariants,
      isLoading: true,
      pollTimer: null,
      recordToDelete: null,
      pageSizeOptions: [25, 50, 75, 100],
      listParams: {
        page: 1,
        perPage: 25,
      },
      orderBy: '',
      sortedBy: '',
      search: '',
      searchFields: '',
      fields: [
        {
          field: 'title',
          label: 'Title',
          filterOptions: {
            enabled: true,
            placeholder: 'Search title',
            trigger: 'enter',
          },
        },
        {
          field: 'source',
          label: 'Source',
          formatFn: value => sourceLabels[value] || value,
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterDropdownItems: Object.entries(sourceLabels).map(([value, text]) => ({ value, text })),
          },
        },
        {
          field: 'status',
          label: 'Status',
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterDropdownItems: Object.entries(statusLabels).map(([value, text]) => ({ value, text })),
          },
        },
        {
          field: 'created_by_name',
          label: 'Created By',
          sortable: false,
        },
        {
          field: 'created_at',
          label: 'Created',
          formatFn: formatDateTime,
        },
        {
          field: 'finished_at',
          label: 'Finished',
          formatFn: formatDateTime,
        },
        {
          field: 'action',
          label: 'Actions',
          sortable: false,
          thClass: 'action-th-width',
        },
      ],
      mobileFields: [
        { field: 'source_label', label: 'Source' },
        { field: 'status_label', label: 'Status' },
        { field: 'created_by_name', label: 'Created By' },
        { field: 'created_at_label', label: 'Created' },
        { field: 'finished_at_label', label: 'Finished' },
        { field: 'action', label: 'Actions' },
      ],
    }
  },
  computed: {
    ...mapState('siteBackups', ['items', 'pagination']),
    rows() {
      return this.items.map(item => ({
        ...item,
        heading: item.title,
        source_label: sourceLabels[item.source] || item.source,
        status_label: statusLabels[item.status] || item.status,
        created_by_name: item.created_by ? item.created_by.name : '-',
        created_at_label: formatDateTime(item.created_at),
        finished_at_label: formatDateTime(item.finished_at),
      }))
    },
    requestParams() {
      return {
        page: this.listParams.page,
        perPage: this.listParams.perPage,
        searchJoin: 'and',
        search: [`site_id:${this.siteId}`, this.search].filter(Boolean).join(';'),
        searchFields: ['site_id:=', this.searchFields].filter(Boolean).join(';'),
        ...(this.orderBy ? { orderBy: this.orderBy, sortedBy: this.sortedBy } : {}),
      }
    },
  },
  created() {
    this.fetchItems()
  },
  beforeDestroy() {
    this.isPollingStopped = true
    clearTimeout(this.pollTimer)
    this.$store.commit('siteBackups/RESET_STATE')
  },
  methods: {
    ...mapActions('siteBackups', ['getItems', 'deleteItem']),
    async fetchItems({ silent = false } = {}) {
      clearTimeout(this.pollTimer)

      if (!silent) {
        this.isLoading = true
      }

      try {
        await this.getItems(this.requestParams)
      } catch (error) {
        console.warn(error)
      } finally {
        this.isLoading = false
      }

      // Backups are created by a queued job, so the list refreshes itself until none is pending.
      if (!this.isPollingStopped && this.items.some(item => item.status === STATUS_PENDING)) {
        this.pollTimer = setTimeout(() => this.fetchItems({ silent: true }), POLL_INTERVAL)
      }
    },
    onSortChange([{ field, type }]) {
      this.orderBy = type === 'none' ? '' : field
      this.sortedBy = type === 'none' ? '' : type
      this.fetchItems()
    },
    onColumnFilter({ columnFilters }) {
      const filters = Object.entries(columnFilters).filter(([field, value]) => filterFields[field] && value)

      this.search = filters.map(([field, value]) => `${filterFields[field].split(':')[0]}:${value}`).join(';')
      this.searchFields = filters.map(([field]) => filterFields[field]).join(';')
      this.listParams.page = 1
      this.fetchItems()
    },
    perPageChanged(value) {
      this.listParams.perPage = value
      this.listParams.page = 1
      this.fetchItems()
    },
    changePage(value) {
      this.listParams.page = value
      this.fetchItems()
    },
    viewBackup(backupId) {
      this.$router.push({ name: 'site-summary-backup-details', params: { id: this.siteId, backupId } })
    },
    deleteHandler(item) {
      this.recordToDelete = item
      this.$refs.modalDelete.show()
    },
    async deleteRow() {
      const { id } = this.recordToDelete

      try {
        await this.deleteItem(id)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Backup deleted',
            icon: 'TrashIcon',
            variant: 'success',
          },
        })
        this.fetchItems()
      } catch (error) {
        console.warn(error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Some list pages leak a global middle alignment for vue-good-table, so without this the look depends on navigation.
.site-backups-table::v-deep .vgt-table {
  th,
  td {
    vertical-align: middle;
  }
}
</style>
